import { useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import {
  inventoryService,
  locationService,
  productService,
  purchaseOrderImportService,
  storeLocationMappingService,
} from "../../services";
import PurchaseOrderAddPricingDialog from "./PurchaseOrderAddPricingDialog";

const DEFAULT_LOCATION_CODE = "DK11";

async function ensureOrgMappedToLocationCode(orgId, locationCode) {
  const code = String(locationCode || DEFAULT_LOCATION_CODE).trim();
  const locRes = await locationService.getLocations({ search: code, limit: 50 });
  const locations = locRes?.data || locRes?.items || locRes || [];
  const list = Array.isArray(locations) ? locations : [];
  const location = list.find(
    (l) => String(l.code || "").toUpperCase() === code.toUpperCase(),
  );
  if (!location?.id) return;

  const linksRes = await storeLocationMappingService.getOrgLocationLinksAll(orgId);
  const links = linksRes?.data || linksRes?.items || linksRes || [];
  const linkList = Array.isArray(links) ? links : [];
  const mappedIds = new Set(
    linkList.flatMap((row) =>
      (row.locations || []).map((loc) => loc.id || loc.location_id),
    ),
  );
  if (mappedIds.has(location.id)) return;

  await storeLocationMappingService.create({
    organization_id: orgId,
    location_ids: [location.id],
  });
}

export default function PurchaseOrderRemediateActions({
  remediation,
  orgId,
  sessionId,
  lineId,
  productName,
  sku,
  adminProductId,
  defaultLocationCode = DEFAULT_LOCATION_CODE,
  mode = "import",
  onSuccess,
  onError,
  disabled = false,
  stackDirection = "column",
}) {
  const [busy, setBusy] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [pricingError, setPricingError] = useState(null);

  const makePurchasable = remediation?.make_purchasable;
  const addPricing = remediation?.add_pricing;
  const pricingLocationCode =
    addPricing?.location_code || defaultLocationCode;

  if (!makePurchasable && !addPricing) return null;

  const handleMakePurchasable = async () => {
    setBusy(true);
    try {
      if (mode === "import") {
        const updated = await purchaseOrderImportService.remediateLine(
          sessionId,
          lineId,
          { action: "make_purchasable" },
        );
        onSuccess?.(updated);
        return;
      }

      const productId = makePurchasable.product_id || adminProductId;
      if (!productId) {
        throw new Error("Product id is required to make purchasable.");
      }
      await productService.updateProduct(productId, { is_purchasable: true });
      onSuccess?.();
    } catch (err) {
      const msg = err.response?.data?.message;
      onError?.(
        Array.isArray(msg) ? msg.join(", ") : msg || err.message || "Action failed",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleAddPricingSubmit = async ({
    unit_cost,
    unit_price,
    quantity_on_hand,
    location_code,
  }) => {
    setBusy(true);
    setPricingError(null);
    try {
      const targetSku = addPricing?.sku || sku;
      if (mode === "import") {
        const updated = await purchaseOrderImportService.remediateLine(
          sessionId,
          lineId,
          {
            action: "add_pricing",
            unit_cost,
            unit_price,
            location_code: location_code || pricingLocationCode,
          },
        );
        setPricingOpen(false);
        onSuccess?.(updated);
        return;
      }

      if (!targetSku) {
        throw new Error("SKU is required to add pricing.");
      }
      await inventoryService.createOrUpdateInventory(targetSku, {
        unit_cost,
        unit_price,
        quantity_on_hand,
        location_code: location_code || pricingLocationCode,
        reason: "Add pricing from Create Purchase Order",
      });
      if (orgId) {
        await ensureOrgMappedToLocationCode(
          orgId,
          location_code || pricingLocationCode,
        );
      }
      setPricingOpen(false);
      onSuccess?.();
    } catch (err) {
      const msg = err.response?.data?.message;
      const message = Array.isArray(msg)
        ? msg.join(", ")
        : msg || err.message || "Failed to save pricing";
      setPricingError(message);
      onError?.(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Stack direction={stackDirection} spacing={1} sx={{ minWidth: 140 }}>
        {makePurchasable ? (
          <Button
            size="small"
            variant="outlined"
            disabled={disabled || busy}
            onClick={handleMakePurchasable}
          >
            Make purchasable
          </Button>
        ) : null}
        {addPricing ? (
          <Button
            size="small"
            variant="contained"
            disabled={disabled || busy}
            onClick={() => {
              setPricingError(null);
              setPricingOpen(true);
            }}
            sx={{ bgcolor: "#e90000", "&:hover": { bgcolor: "#c00000" } }}
          >
            Add pricing
          </Button>
        ) : null}
        {busy && !pricingOpen ? (
          <Typography variant="caption" color="text.secondary">
            Updating…
          </Typography>
        ) : null}
      </Stack>

      <PurchaseOrderAddPricingDialog
        open={pricingOpen}
        product={{
          sku: addPricing?.sku || sku,
          product_name: productName,
          admin_product_id: addPricing?.admin_product_id || adminProductId,
        }}
        defaultLocationCode={pricingLocationCode}
        submitting={busy}
        error={pricingError}
        submitLabel="Save and re-resolve"
        onClose={() => {
          if (busy) return;
          setPricingOpen(false);
          setPricingError(null);
        }}
        onSubmit={handleAddPricingSubmit}
      />
    </>
  );
}
